
import React, { useState, useEffect, useCallback } from 'react';
import { S3ListItem, S3Folder, S3File, ActiveFilter, StarType, CustomFolder } from '../types';
import { listS3Items } from '../services/s3Service';
import LoadingSpinner from './LoadingSpinner';
import { starIconsMap } from './StarPicker'; 
import { formatImageSuffix } from '../App'; 
import {
  GENERATED_IMAGES_FASE_PATH,
  EXPORTED_IMAGES_FASE_PATH,
  IMAGE_FILE_EXTENSIONS,
  CATEGORY_TO_FASE_MAPPING,
  USER_CREATED_WORK_SPANISH_NAME,
  USER_EXPORTED_ART_SPANISH_NAME,
  TOP_LEVEL_CATEGORIES,
} from '../constants';

interface ImageBrowserProps {
  onSelectFile: (file: S3File) => void;
  selectedFileId: string | null;
  targetPath?: string;
  isUserGalleryActive: boolean;
  username: string; 
  customFolders: CustomFolder[]; 
  activeFilter: ActiveFilter | null;
  onClearFilter: () => void;
  onSetBrowsePath: (path: string) => void;
  onDebugHover?: (name: string | null) => void; // New prop for debug hover
}

interface Breadcrumb {
  name: string;
  path: string;
  isFilterCrumb?: boolean;
  filterStarType?: StarType; 
}

const FolderIcon: React.FC<{className?: string}> = ({className}) => (
  <svg className={className || "w-5 h-5 mr-3 text-indigo-400 flex-shrink-0"} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" >
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z" />
  </svg>
);

interface PreviewState {
  url: string;
  nameWithSuffix: string; 
  top: number;
  left: number;
  width: number;
  height: number;
}

const ImageBrowser: React.FC<ImageBrowserProps> = ({
  onSelectFile,
  selectedFileId,
  targetPath,
  isUserGalleryActive,
  username,
  customFolders, 
  activeFilter,
  onClearFilter,
  onSetBrowsePath,
  onDebugHover, 
}) => {
  const [items, setItems] = useState<S3ListItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<PreviewState | null>(null);
  const [filteredItemsCount, setFilteredItemsCount] = useState<number | null>(null);

  const buildBreadcrumbs = useCallback((pathForCrumbs: string, currentActiveFilter: ActiveFilter | null): Breadcrumb[] => {
    const rootCrumbName = isUserGalleryActive ? username : "ArteAI";
    const crumbs: Breadcrumb[] = [{ name: rootCrumbName, path: "" }];

    if (currentActiveFilter) {
      const filterCrumb: Breadcrumb = { 
        name: currentActiveFilter.displayName, 
        path: "filter_results", 
        isFilterCrumb: true 
      };
      if (currentActiveFilter.type === 'star') {
        filterCrumb.filterStarType = currentActiveFilter.value as StarType;
      }
      crumbs.push(filterCrumb);
      return crumbs;
    }

    const actualPathForCrumbs = pathForCrumbs === "filter_view" || pathForCrumbs === "filter_view_star" ? "" : pathForCrumbs;

    if (actualPathForCrumbs === "") { 
      return crumbs;
    }

    const parts = actualPathForCrumbs.split('/').filter(Boolean);
    let currentCrumbPathAccumulator = "";

    parts.forEach((part, index) => {
      currentCrumbPathAccumulator += part + "/";
      let displayName = part;

      if (isUserGalleryActive) {
        if (index === 0 && currentCrumbPathAccumulator === GENERATED_IMAGES_FASE_PATH + "/" + username + "/") {
          displayName = USER_CREATED_WORK_SPANISH_NAME; 
        } else if (index === 0 && currentCrumbPathAccumulator === EXPORTED_IMAGES_FASE_PATH + "/" + username + "/") {
          displayName = USER_EXPORTED_ART_SPANISH_NAME; 
        } 
      } else {
        if (index === 0) { 
          const categoryKey = Object.keys(CATEGORY_TO_FASE_MAPPING).find(
              key => CATEGORY_TO_FASE_MAPPING[key] === part && TOP_LEVEL_CATEGORIES.includes(key)
          );
          if (categoryKey) {
              displayName = categoryKey;
          }
        } else if (index === 1) { 
            displayName = part; 
        }
      }
      if (!(isUserGalleryActive && index === 0 && part === username && parts.length > 1 && (parts[0] === GENERATED_IMAGES_FASE_PATH || parts[0] === EXPORTED_IMAGES_FASE_PATH)  )) {
         if (displayName !== username || !isUserGalleryActive) { 
            crumbs.push({ name: displayName, path: currentCrumbPathAccumulator });
         }
      }
    });
    return crumbs;
  }, [isUserGalleryActive, username, customFolders]); 

  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>(() => buildBreadcrumbs(activeFilter ? "filter_view" : (targetPath || ""), activeFilter));

  const loadItems = useCallback(async (pathForLoading: string, filterForLoading: ActiveFilter | null) => {
    setIsLoading(true);
    setError(null);
    setItems([]); 
    setFilteredItemsCount(null); 

    try {
      const fetchedItems = await listS3Items(pathForLoading, isUserGalleryActive, filterForLoading || undefined, username, customFolders);
      let processedDisplayItems: S3ListItem[] = [];
      let currentImageFileCount = 0;

      if (filterForLoading) {
        fetchedItems.forEach(item => {
          if (item.itemType === 'file') {
            processedDisplayItems.push(item); 
            currentImageFileCount++;
          }
        });
        setFilteredItemsCount(currentImageFileCount);
      } else {
        fetchedItems.forEach(item => {
          if (item.itemType === 'folder') {
            processedDisplayItems.push(item);
          } else if (item.itemType === 'file') {
            const s3File = item as S3File; 
            const lowerCaseName = s3File.name.toLowerCase();
            if (IMAGE_FILE_EXTENSIONS.some(ext => lowerCaseName.endsWith(ext))) {
              processedDisplayItems.push(s3File);
            }
          }
        });
      }
      setItems(processedDisplayItems);

    } catch (err) {
      console.error("Error al cargar elementos S3:", err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(`Error al cargar elementos. ${errorMessage}`);
      setItems([]); 
    } finally {
      setIsLoading(false);
    }
  }, [isUserGalleryActive, username, customFolders]); 

  useEffect(() => {
    const pathForEffect = activeFilter ? (activeFilter.type === 'star' ? "filter_view_star" : "filter_view") : (targetPath || "");
    loadItems(pathForEffect, activeFilter);
    setBreadcrumbs(buildBreadcrumbs(pathForEffect, activeFilter));
  }, [targetPath, activeFilter, loadItems, buildBreadcrumbs]);

  const handleFolderClick = (folder: S3Folder) => {
    onSetBrowsePath(folder.path);
  };

  const handleBreadcrumbClick = (crumb: Breadcrumb) => {
    if (crumb.isFilterCrumb) return;
    if ((crumb.path === "" || crumb.path === "filter_view" || crumb.path === "filter_view_star") && activeFilter) {
      onClearFilter();
    } else {
      onSetBrowsePath(crumb.path);
    }
  };

  const handleShowPreview = (file: S3File, event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const suffix = formatImageSuffix(file.parsedDate, file.parsedTime, file.parsedMachineCode) || "";
    setPreviewImage({
      url: file.url,
      nameWithSuffix: `${file.displayName || file.name}${suffix}`,
      top: rect.top,
      left: rect.right + 10,
      width: 200,
      height: 150,
    });
  };

  const handleHidePreview = () => {
    setPreviewImage(null);
  };

  if (error) {
    return <p className="text-red-400 p-4">{error}</p>;
  }

  return (
    <div className="h-full flex flex-col">
      <div className="p-2 border-b border-gray-700">
        <nav aria-label="Breadcrumb" className="flex space-x-1 text-sm overflow-x-auto whitespace-nowrap pb-1">
          {breadcrumbs.map((crumb, index) => {
            const isArteAIRootCrumb = !isUserGalleryActive && index === 0 && crumb.name === "ArteAI";
            const isUserGalleryRootCrumb = isUserGalleryActive && index === 0 && crumb.name === username;
            const isLastCrumb = index === breadcrumbs.length - 1;

            let buttonBaseClasses = "rounded-md px-2 py-1 ";
            let buttonTextClasses = "";
            let buttonHoverClasses = "";

            if (isArteAIRootCrumb || isUserGalleryRootCrumb) {
              buttonTextClasses = "text-yellow-400 font-bold ";
              if (!isLastCrumb || crumb.isFilterCrumb) {
                buttonHoverClasses = "hover:bg-gray-700 hover:text-yellow-300 ";
              }
            } else if (isLastCrumb && !crumb.isFilterCrumb) {
              buttonTextClasses = "text-indigo-400 font-semibold ";
            } else if (crumb.isFilterCrumb) {
              buttonTextClasses = "text-green-400 font-semibold ";
            }
            else {
              buttonTextClasses = "text-gray-400 ";
              buttonHoverClasses = "hover:bg-gray-700 hover:text-gray-200 ";
            }
            const finalButtonClassName = `${buttonBaseClasses} ${buttonTextClasses} ${buttonHoverClasses}`;
            const starInfo = crumb.isFilterCrumb && crumb.filterStarType ? starIconsMap[crumb.filterStarType] : null;
            const IconComponent = starInfo?.icon;

            return (
              <React.Fragment key={crumb.path + '-' + index + '-' + crumb.name}>
                {index > 0 && <span className="text-gray-500 mx-1">/</span>}
                <button
                  onClick={() => handleBreadcrumbClick(crumb)}
                  onMouseEnter={() => onDebugHover?.(`Breadcrumb: ${crumb.name}`)}
                  onMouseLeave={() => onDebugHover?.(null)}
                  className={`${finalButtonClassName.trim()} inline-flex items-center`}
                  aria-current={isLastCrumb && !crumb.isFilterCrumb ? "page" : undefined}
                  disabled={(isLastCrumb && crumb.isFilterCrumb)}
                >
                  {crumb.name}
                  {IconComponent && (
                    <IconComponent className={`w-3.5 h-3.5 ml-1.5 ${starInfo?.color.split(' ')[0]}`} />
                  )}
                </button>
              </React.Fragment>
            );
          })}
        </nav>
      </div>

      {activeFilter && filteredItemsCount !== null && !isLoading && (
        <div className="px-3 pt-1 pb-2 text-xs text-gray-400 border-b border-gray-700 mb-2">
          Mostrando {filteredItemsCount} {filteredItemsCount === 1 ? 'imagen' : 'imágenes'}.
          <button 
            onClick={onClearFilter} 
            onMouseEnter={() => onDebugHover?.("Button: Clear Filter")}
            onMouseLeave={() => onDebugHover?.(null)}
            className="ml-2 text-indigo-400 hover:text-indigo-300 underline"
          >
            Limpiar filtro
          </button>
        </div>
      )}

      {isLoading && <LoadingSpinner text="Cargando elementos..." />}

      {!isLoading && items.length === 0 && !error && (
        <div className="text-center p-4 text-gray-400">
            <p className="text-lg mb-2">
                {activeFilter ? (
                    <>
                        No se encontraron imágenes para "{activeFilter.displayName}"
                        {activeFilter.type === 'star' && starIconsMap[activeFilter.value as StarType] &&
                            React.createElement(starIconsMap[activeFilter.value as StarType].icon, {
                            className: `w-4 h-4 inline-block ml-1.5 align-middle ${starIconsMap[activeFilter.value as StarType].color.split(' ')[0]}`
                            })
                        }
                        .
                    </>
                ) : "Esta carpeta está vacía."}
            </p>
            <p className="text-sm">Intenta con otra carpeta o ajusta tus filtros.</p>
        </div>
      )}

      {!isLoading && items.length > 0 && (
        <div className="flex-grow overflow-y-auto p-2 space-y-1.5 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
          {items.map((item) => {
            if (item.itemType === 'folder' && !activeFilter) { 
              return (
                <div
                  key={item.id}
                  onClick={() => handleFolderClick(item as S3Folder)}
                  onMouseEnter={() => onDebugHover?.(`Folder: ${item.name}`)}
                  onMouseLeave={() => onDebugHover?.(null)}
                  className="flex items-center p-2.5 rounded-md text-gray-200 hover:bg-gray-700 hover:text-indigo-300 cursor-pointer transition-colors duration-150 ease-in-out focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => e.key === 'Enter' && handleFolderClick(item as S3Folder)}
                  aria-label={`Abrir carpeta ${item.name}`}
                >
                  <FolderIcon />
                  <span className="truncate" title={item.name}>{item.name}</span>
                </div>
              );
            } else if (item.itemType === 'file') {
              const s3File = item as S3File;
              const isSelected = selectedFileId === s3File.id;
              const suffix = formatImageSuffix(s3File.parsedDate, s3File.parsedTime, s3File.parsedMachineCode);
              const titleText = `${s3File.displayName || s3File.name}${suffix || ''}`;
              return (
                <div
                  key={s3File.id}
                  onClick={() => onSelectFile(s3File)}
                  onMouseEnter={(e) => {
                    handleShowPreview(s3File, e);
                    onDebugHover?.(`File: ${titleText}`);
                  }}
                  onMouseLeave={() => {
                    handleHidePreview();
                    onDebugHover?.(null);
                  }}
                  className={`flex items-center p-2.5 rounded-md cursor-pointer transition-colors duration-150 ease-in-out focus:outline-none focus:ring-1 focus:ring-indigo-500
                    ${isSelected ? 'bg-indigo-600 text-white font-semibold' : 'text-gray-300 hover:bg-gray-700 hover:text-sky-300'}`}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => e.key === 'Enter' && onSelectFile(s3File)}
                  aria-label={`Seleccionar imagen ${titleText}`}
                  aria-pressed={isSelected}
                  title={titleText} 
                >
                  <img 
                    src={s3File.url} 
                    alt={`Miniatura de ${s3File.displayName || s3File.name}`}
                    className="w-10 h-10 object-cover rounded-sm mr-3 flex-shrink-0 border border-gray-700"
                    loading="lazy"
                  />
                  <span className="truncate">
                    {s3File.displayName || s3File.name}
                    {suffix && <span className="ml-1.5 text-xs text-gray-500">{suffix}</span>}
                  </span>
                </div>
              );
            }
            return null;
          })}
        </div>
      )}

      {previewImage && (
        <div
          style={{
            top: `${previewImage.top}px`,
            left: `${previewImage.left}px`,
            maxWidth: `${previewImage.width}px`,
            maxHeight: `${previewImage.height}px`,
          }}
          className="fixed z-50 p-1.5 bg-gray-900 border border-indigo-500 rounded-md shadow-2xl pointer-events-none animate-fadeIn"
        >
          <img
            src={previewImage.url}
            alt={previewImage.nameWithSuffix} 
            className="object-contain w-full h-full rounded"
            style={{
                maxWidth: `${previewImage.width}px`,
                maxHeight: `${previewImage.height - 20}px`, 
            }}
          />
          <p className="text-xs text-center text-gray-300 mt-1 truncate bg-gray-900 bg-opacity-75 px-1 py-0.5" title={previewImage.nameWithSuffix}>
            {previewImage.nameWithSuffix}
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageBrowser;
